import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useQuery } from 'react-query';
import InfoIcon from '@mui/icons-material/Info';
import { AvailablePhotographer, CustomerRebookingResult } from '../../shared/types';
import { AuthenticatedUser } from '../../../../../../shared/types';
import { useWorkaroundBodyBackground } from '../../../../../../shared/hooks/workaroundBodyBackground';
import { useTelemetryContext } from '../../../../../../telemetry/telemetryContext';
import { Heading } from '../../shared/view/Heading';
import { Translate } from '../../../../../../shared/LanguageService';
import { CustomerPhotographerBookingApi } from '../../shared/queries/CustomerPhotographerBookingApi';
import { getUserInfo } from '../../../../../../telemetry/utils';
import { CustomerBookingMobileSkeleton } from '../../shared/view/CustomerBookingMobileSkeleton';
import { useRebookOrderMutation } from '../queries/rebookingRequestMutation';
import FailureSnackbar from '../../../../../../shared/feedback/FailureSnackbar';
import {
    BookableDays,
    getNextWorkday,
    getStartBookingDate
} from '../../shared/bookingDateSelect/state/dateHelper';
import {
    noBookableDays,
    useBookableDaysFrom
} from '../../shared/bookingDateSelect/state/useBookableDaysFrom';
import {
    BookingMethodSection,
    BookingMethod
} from '../../shared/timeCentric/view/BookingMethodSection';
import { TimeslotCentric } from '../../shared/timeCentric/view/TimeslotCentric';
import { PhotographerCentric } from '../../shared/photographerCentric/view/PhotographerCentric';
import {
    manageSavedBookingMethod,
    usePersistBookingMethodSelection,
    clearPersistedBookingMethod
} from '../../shared/state/bookingMethodUtil';
import { clearPersistedPhotographer } from '../../shared/photographerCentric/state/photographerUtils';

type CustomerRebookingCalendarMobileProps = {
    availablePhotographers: AvailablePhotographer[];
    displayingBookingDate: Date;
    earliestBookableDate?: Date | null;
    onBook: (photographerId: number, timeslot: string, planOrderId: number) => void;
    onClickBackButton: () => void;
    onChangeDisplayingBookingDate: (dateTime: Date) => void;
    planOrderId: number | null;
};

type ExternalProps = {
    redirectTo: string;
    orderId: number;
    user: AuthenticatedUser;
};

function CustomerRebookingCalendarMobile({
    availablePhotographers,
    displayingBookingDate,
    earliestBookableDate = null,
    onBook,
    onClickBackButton,
    onChangeDisplayingBookingDate,
    planOrderId
}: CustomerRebookingCalendarMobileProps) {
    useWorkaroundBodyBackground('rgba(250, 250, 250, 1)');
    let [showHints, setShowHints] = React.useState(false);
    let [bookingMethod, setBookingMethod] = React.useState(BookingMethod.TimeCentric);

    usePersistBookingMethodSelection(
        bookingMethod,
        (newBookingMethod: BookingMethod) => {
            if (newBookingMethod !== bookingMethod) {
                setBookingMethod(newBookingMethod);
            }
        },
        [bookingMethod]
    );

    function handleChangeBookingMethod(newBookingMethod: BookingMethod) {
        manageSavedBookingMethod(newBookingMethod);

        setBookingMethod(newBookingMethod);
    }

    function handleRebookFactory(
        planOrderId: number | null,
        photographerId: number | null
    ): (timeslot: string) => void {
        return function handleRebook(timeslot: string) {
            if (photographerId) {
                clearPersistedBookingMethod();
                clearPersistedPhotographer();
                onBook(photographerId, timeslot, planOrderId);
            }
        };
    }

    function handleClickBackButton() {
        clearPersistedBookingMethod();
        clearPersistedPhotographer();
        onClickBackButton();
    }

    return (
        <Box
            component="section"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%'
            }}
        >
            <Box
                component="span"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px'
                }}
            >
                <IconButton onClick={handleClickBackButton}>
                    <KeyboardBackspaceIcon />
                </IconButton>
                <Heading>{Translate('se360/photographerbooking/customer/calendar/title')}</Heading>
                <IconButton
                    onClick={() => {
                        setShowHints((prev) => !prev);
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    <InfoIcon sx={{ color: 'secondary.dark' }} />
                </IconButton>
            </Box>
            <BookingMethodSection
                showHint={showHints}
                onChange={handleChangeBookingMethod}
                value={bookingMethod}
            />
            {bookingMethod === BookingMethod.TimeCentric ? (
                <TimeslotCentric
                    allAvailablePhotographers={availablePhotographers}
                    displayingBookingDate={displayingBookingDate}
                    handleBookFactory={handleRebookFactory}
                    onChangeDisplayingBookingDate={onChangeDisplayingBookingDate}
                    planOrderId={planOrderId}
                    showHints={showHints}
                    earliestBookableDate={earliestBookableDate}
                />
            ) : (
                <PhotographerCentric
                    availablePhotographers={availablePhotographers}
                    displayingBookingDate={displayingBookingDate}
                    handleBookFactory={handleRebookFactory}
                    onChangeDisplayingBookingDate={onChangeDisplayingBookingDate}
                    planOrderId={planOrderId}
                    showHints={showHints}
                    earliestBookableDate={earliestBookableDate}
                />
            )}
        </Box>
    );
}

export let CustomerRebookingMobileQueryKey = 'CustomerRebookingMobileQuery';

export default withRouter(function CustomerRebookingCalendarMobileDataLayer({
    user,
    orderId,
    redirectTo,
    history
}: ExternalProps & RouteComponentProps) {
    let [defaultBookingDate] = React.useState(() => getNextWorkday(new Date(Date.now())));
    let [wantedBookingDate, setWantedBookingDate] = React.useState<Date | null>(null);
    let [bookableDays, setBookableDays] = React.useState<BookableDays>(noBookableDays);
    let [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
    let { logger } = useTelemetryContext();

    let displayingBookingDate = getStartBookingDate(
        wantedBookingDate,
        defaultBookingDate,
        bookableDays
    );

    let { data, isError, error, isLoading } = useQuery<CustomerRebookingResult>(
        [CustomerRebookingMobileQueryKey, displayingBookingDate],
        () =>
            CustomerPhotographerBookingApi.getPhotographersForCustomerRebooking(
                orderId,
                displayingBookingDate
            ),
        {
            onError: (err: unknown) => {
                let additionalInfo = {
                    origin: 'CustomerBookingCalendarMobile.getPhotographersForNewCustomerBooking',
                    userInfo: getUserInfo(user)
                };

                logger.logException(err, additionalInfo);
            }
        }
    );
    let isTakingOverBookableDays = useBookableDaysFrom(data, bookableDays, setBookableDays);

    let rebookOrderMutation = useRebookOrderMutation(orderId);

    async function handleRebooking(photographerId: number, timeslot: string, planOrderId: number) {
        try {
            await rebookOrderMutation.mutateAsync({ timeslot, photographerId, planOrderId });
            history.push(redirectTo);
        } catch (err) {
            let additionalInfo = {
                origin: 'CustomerRebookingCalendarMobile.handleRebooking',
                userInfo: getUserInfo(user)
            };

            logger.logException(err, additionalInfo);
            setShowErrorSnackbar(true);
            setWantedBookingDate(null);
        }
    }

    if (isLoading || isTakingOverBookableDays) {
        return <CustomerBookingMobileSkeleton />;
    }

    if (isError) {
        let additionalInfo = {
            origin: 'CustomerBookingCalendarMobile.getPhotographersForNewCustomerBooking',
            userInfo: getUserInfo(user)
        };

        logger.logException(error, additionalInfo);
    }

    let { availablePhotographers, planOrderId } = data || {
        availablePhotographers: [],
        planOrderId: null
    };

    return (
        <>
            <CustomerRebookingCalendarMobile
                availablePhotographers={availablePhotographers}
                displayingBookingDate={displayingBookingDate}
                earliestBookableDate={bookableDays.earliestBookableDate}
                onBook={handleRebooking}
                planOrderId={planOrderId}
                onClickBackButton={() => {
                    history.push(redirectTo);
                }}
                onChangeDisplayingBookingDate={setWantedBookingDate}
            />
            <FailureSnackbar
                open={showErrorSnackbar}
                onClose={() => {
                    setShowErrorSnackbar(false);
                }}
                text={Translate('se360/photographerbooking/customer/rebook/failure')}
            />
        </>
    );
});
